import type { ReactNode } from "react";
import { Form, Link } from "react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import type { Me, Profile } from "@repo/shared-types";
import { requireSessionWith } from "@/lib/guards";
import { queries } from "@/lib/queries";
import { read } from "@/lib/query";
import { useSession } from "@/routes/root";
import { divisionLabel } from "@/lib/results";
import { Avatar } from "@/components/avatar";
import { PageTitle } from "@/components/brand";
import { TopBar } from "@/components/navigation";
import { useAppearance } from "@/components/theme";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Item, ItemContent, ItemMedia, ItemTitle } from "@/components/ui/item";
import { cn } from "@/lib/utils";

/**
 * Account, the You tab, as a phone's settings are (ADR 0068): you at the top,
 * opening Profile, then a row for each setting, showing its value where it
 * has one, each opening a page of its own. No page explains itself, except
 * Delete account's. What those pages share is in `account-section.tsx`.
 */

export async function accountLoader({ request }: { request: Request }) {
  await requireSessionWith(request, () => read(queries.profile()));
  return null;
}

export default function Account() {
  const session = useSession();
  const { data: profile } = useSuspenseQuery(queries.profile());
  // The loader let the page draw only with a session.
  return session && <AccountPage session={session} profile={profile} />;
}

function AccountPage({ session, profile }: { session: Me; profile: Profile }) {
  const { user } = session;
  const appearance = useAppearance();

  return (
    <section className="space-y-6">
      <PageTitle of={["Account"]} />
      <TopBar>
        <h1 className="page-title">Account</h1>
      </TopBar>

      <Card className="gap-0 p-0">
        <Item asChild className="rounded-none px-6 py-5">
          <Link to="/account/profile">
            <ItemMedia>
              <Avatar person={user} size="md" />
            </ItemMedia>
            <ItemContent className="min-w-0">
              <ItemTitle className="text-base wrap-anywhere">@{user.username}</ItemTitle>
              <span className="truncate text-muted-foreground">{user.email}</span>
            </ItemContent>
            <Chevron />
          </Link>
        </Item>
      </Card>

      <Card className="gap-0 divide-y p-0">
        <Row to="/account/password">Password</Row>
      </Card>

      <Card className="gap-0 divide-y p-0">
        <Row to="/account/division" value={divisionLabel(profile.division) || "Not set"}>
          Division
        </Row>
        <Row to="/account/time-zone" value={profile.timeZone ?? "This device's"}>
          Time zone
        </Row>
        <Row to="/account/appearance" value={appearance.label}>
          Appearance
        </Row>
      </Card>

      <Card className="gap-0 divide-y p-0">
        {/* Only where a deployment takes feedback: the board needs the
            address that runs it configured (0091). */}
        {session.feedback && <Row to="/feedback">Feedback</Row>}
        {/* Signing out lives here since the header went (0052): the tab bar
            holds where to go, not what to do. */}
        <Form method="post" action="/sign-out">
          <Button
            type="submit"
            variant="ghost"
            className="h-auto w-full justify-start rounded-none px-6 py-5 text-base font-medium"
          >
            Sign out
          </Button>
        </Form>
      </Card>

      <Card className="gap-0 p-0 ring-destructive/30 dark:ring-destructive/40">
        <Row to="/account/delete" danger>
          Delete account
        </Row>
      </Card>
    </section>
  );
}

function Chevron() {
  return <ChevronRight aria-hidden="true" className="size-5 shrink-0 text-muted-foreground" />;
}

/** A row of the list: what it opens, and the value it holds when it holds one. */
function Row({ to, value, danger = false, children }: { to: string; value?: string; danger?: boolean; children: ReactNode }) {
  return (
    <Item asChild className="flex-nowrap rounded-none px-6 py-5">
      <Link to={to}>
        <ItemContent>
          <ItemTitle className={cn("text-base", danger && "text-destructive")}>{children}</ItemTitle>
        </ItemContent>
        {value && <span className="min-w-0 truncate text-muted-foreground">{value}</span>}
        <Chevron />
      </Link>
    </Item>
  );
}
